import * as Config from "effect/Config";
import * as Data from "effect/Data";
import * as Effect from "effect/Effect";
import { pipe } from "effect/Function";
import { SingleShotGen } from "effect/Utils";
import { getRefMetadata, isRef } from "./Ref.js";
import { isResource } from "./Resource.js";
import { RuntimeContext, sanitizeKey } from "./RuntimeContext.js";
import { Stack } from "./Stack.js";
import { Stage } from "./Stage.js";
import * as State from "./State/State.js";
import { isPlainData, isPrimitive } from "./Util/data.js";
const inspect = Symbol.for("nodejs.util.inspect.custom");
export const of = (resource) => {
    if (isRef(resource)) {
        const metadata = getRefMetadata(resource);
        return new RefExpr(metadata.stack, metadata.stage, metadata.id, 
        // Surface the target's resource type and logical id as
        // statically-known properties so duck-typing classifiers (Worker
        // env bindings) and label/bind templates (`${resource.LogicalId}`,
        // `host.bind\`...\``) identify the ref exactly like a
        // locally-declared resource.
        {
            LogicalId: metadata.id,
            ...(metadata.type !== undefined ? { Type: metadata.type } : {}),
        });
    }
    return new ResourceExpr(resource);
};
export const asOutput = (t) => isOutput(t)
    ? t
    : Effect.isEffect(t)
        ? new EffectExpr(VoidExpr, () => t)
        : new LiteralExpr(t);
/**
 * Lift a plan-time Effect into an {@link Output}.
 *
 * The effect runs when the stack resolves the Output during plan/deploy —
 * with the stack's services (cloud credentials, region, ...) provided — and
 * never inside a deployed runtime: constructing the Output is inert, so
 * helpers built on `fromEffect` (e.g. AMI lookups) are safe to call from
 * composition code that is re-executed inside a Function/Worker/Instance
 * bundle.
 *
 * The effect must not fail (`E = never`) — die with a descriptive error for
 * unresolvable lookups.
 */
export const fromEffect = (effect) => new EffectExpr(VoidExpr, () => effect);
export const isOutput = (value) => value &&
    (typeof value === "object" || typeof value === "function") &&
    ExprSymbol in value;
export const ExprSymbol = Symbol.for("alchemy/Expr");
const exprKind = (node) => node?.[ExprSymbol]?.kind ?? node?.kind;
export const isExpr = (value) => value &&
    (typeof value === "object" || typeof value === "function") &&
    ExprSymbol in value;
export class BaseExpr {
    // we use a kind tag instead of instanceof to protect ourselves from duplicate alchemy module imports
    constructor() { }
    as() {
        return this;
    }
    [Symbol.iterator]() {
        // @ts-expect-error - TODO(sam): fix this (works at runtime, but maybe indicates a bad assumption)
        return new SingleShotGen(this.asEffect());
    }
    asEffect() {
        return this.bind(this.toString());
    }
    bind(id) {
        // `set`/`get` store keys verbatim, so canonicalize here (the caller's job).
        const key = sanitizeKey(id);
        return RuntimeContext.pipe(Effect.flatMap((ctx) => Effect.map(ctx.set(key, this), (k) => ctx.get(k))));
    }
    pipe(...fns) {
        // @ts-expect-error
        return pipe(this, ...fns);
    }
    toString() {
        return this[inspect]();
    }
}
export const isResourceExpr = (node) => exprKind(node) === "ResourceExpr";
export class ResourceExpr extends BaseExpr {
    src;
    stables;
    kind = "ResourceExpr";
    constructor(src, stables) {
        super();
        this.src = src;
        this.stables = stables;
        return proxy(this);
    }
    [inspect]() {
        return this.src.LogicalId;
    }
}
export const isPropExpr = (node) => exprKind(node) === "PropExpr";
export class PropExpr extends BaseExpr {
    expr;
    identifier;
    kind = "PropExpr";
    constructor(expr, identifier) {
        super();
        this.expr = expr;
        this.identifier = identifier;
        return proxy(this);
    }
    [inspect]() {
        return `${this.expr[inspect]()}.${this.identifier.toString()}`;
    }
}
export const literal = (value) => new LiteralExpr(value);
export const isLiteralExpr = (node) => exprKind(node) === "LiteralExpr";
export class LiteralExpr extends BaseExpr {
    value;
    kind = "LiteralExpr";
    constructor(value) {
        super();
        this.value = value;
        return proxy(this);
    }
    [inspect]() {
        return String(this.value);
    }
}
export const VoidExpr = new LiteralExpr(void 0);
export const map = ((...args) => args.length === 1
    ? (output) => new ApplyExpr(output, args[0])
    : new ApplyExpr(args[0], args[1]));
//Output.ApplyExpr<any, any, ResourceLike, any>
export const isApplyExpr = (node) => exprKind(node) === "ApplyExpr";
export class ApplyExpr extends BaseExpr {
    expr;
    f;
    kind = "ApplyExpr";
    constructor(expr, f) {
        super();
        this.expr = expr;
        this.f = f;
        return proxy(this);
    }
    [inspect]() {
        return `${this.expr[inspect]()}.map(${this.f.toString()})`;
    }
}
export const mapEffect = (fn) => (output) => new EffectExpr(output, fn);
export const flatMap = ((...args) => args.length === 1
    ? (output) => new FlatMapExpr(output, args[0])
    : new FlatMapExpr(args[0], args[1]));
export const isFlatMapExpr = (node) => exprKind(node) === "FlatMapExpr";
export class FlatMapExpr extends BaseExpr {
    expr;
    f;
    kind = "FlatMapExpr";
    constructor(expr, f) {
        super();
        this.expr = expr;
        this.f = f;
        return proxy(this);
    }
    [inspect]() {
        return `${this.expr[inspect]()}.flatMap(${this.f.toString()})`;
    }
}
export const isEffectExpr = (node) => exprKind(node) === "EffectExpr";
export class EffectExpr extends BaseExpr {
    expr;
    f;
    kind = "EffectExpr";
    constructor(expr, f) {
        super();
        this.expr = expr;
        this.f = f;
        return proxy(this);
    }
    [inspect]() {
        return `${this.expr[inspect]()}.mapEffect(${this.f.toString()})`;
    }
}
export const isNamedExpr = (node) => exprKind(node) === "NamedExpr";
/**
 * Wraps another `Expr` and overrides its `toString()` / inspect output.
 *
 * `BaseExpr` derives the binding id from `this.toString()`, so
 * wrapping an expression in `NamedExpr` makes that derived id stable and
 * caller-controlled (e.g. an env var name like `"API_KEY"`).
 */
export class NamedExpr extends BaseExpr {
    expr;
    bindingName;
    kind = "NamedExpr";
    constructor(expr, bindingName) {
        super();
        this.expr = expr;
        this.bindingName = bindingName;
        return proxy(this);
    }
    [inspect]() {
        return this.bindingName;
    }
}
export const named = (expr, name) => new NamedExpr(expr, name);
export const all = (...outs) => new AllExpr(outs);
export const isAllExpr = (node) => exprKind(node) === "AllExpr";
export class AllExpr extends BaseExpr {
    outs;
    kind = "AllExpr";
    constructor(outs) {
        super();
        this.outs = outs;
        return proxy(this);
    }
    [inspect]() {
        return `all(${this.outs.map((out) => out[inspect]()).join(", ")})`;
    }
}
export const isRefExpr = (node) => exprKind(node) === "RefExpr";
export class RefExpr extends BaseExpr {
    stack;
    stage;
    resourceId;
    stables;
    kind = "RefExpr";
    constructor(stack, stage, resourceId, 
    /**
     * Statically-known properties of the ref's target (currently its
     * resource `Type`), served as literals by the proxy instead of
     * `PropExpr`s — mirrors {@link ResourceExpr}'s `stables`.
     */
    stables) {
        super();
        this.stack = stack;
        this.stage = stage;
        this.resourceId = resourceId;
        this.stables = stables;
        return proxy(this);
    }
    [inspect]() {
        return `ref(${this.resourceId}, { stack: ${this.stack}, stage: ${this.stage} })`;
    }
}
export const isStackRefExpr = (node) => exprKind(node) === "StackRefExpr";
/**
 * A reference to the persisted output of a Stack at `(stack, stage)`.
 *
 * Resolved at evaluation time by reading `state.getOutput({ stack,
 * stage })`. Distinct from {@link RefExpr}, which references a
 * single resource's attributes within a stack/stage. `stage` may be
 * `undefined`, in which case it falls back to the current stage.
 */
export class StackRefExpr extends BaseExpr {
    stack;
    stage;
    kind = "StackRefExpr";
    constructor(stack, stage) {
        super();
        this.stack = stack;
        this.stage = stage;
        return proxy(this);
    }
    [inspect]() {
        return `stackRef(${this.stack}${this.stage ? `, { stage: ${this.stage} }` : ""})`;
    }
}
/**
 * Build an `Output<A>` referencing the persisted output of another
 * Stack. The returned Effect resolves to a lazy `Output<A>` whose
 * value is read from the state store at plan/apply time.
 *
 * Returns `Effect<Output<A>>` (not `Output<A>` directly) so that
 * `yield* Output.stackRef(...)` reads ergonomically inside an Effect
 * generator and lines up with `Resource.ref` and `Stack.stage.<name>`.
 */
export const stackRef = (stack, options = {}) => Effect.succeed(new StackRefExpr(stack, options.stage));
export const filter = (...outs) => outs.filter(isOutput);
export const interpolate = (template, ...args) => all(...args.map((arg) => (isOutput(arg) ? arg : literal(arg)))).pipe(map((args) => template
    .map((str, i) => str + (args[i] == null ? "" : String(args[i])))
    .join("")));
function proxy(self) {
    const target = Object.assign(() => { }, self);
    if (inspect in self) {
        Object.defineProperty(target, inspect, {
            value: self[inspect].bind(self),
            configurable: true,
        });
    }
    const proxy = new Proxy(target, {
        has: (_, prop) => prop === ExprSymbol || prop === inspect
            ? true
            : // Statically-known literal props (`Type`, `LogicalId` on
                // resource/ref exprs) are visible to `in` checks so duck-typing
                // code paths (e.g. `"LogicalId" in arg`) treat them like real
                // properties, matching what `get` serves.
                ((isResourceExpr(self) || isRefExpr(self)) &&
                    self.stables !== undefined &&
                    prop in self.stables) ||
                    prop in self,
        get: (target, prop) => prop === Symbol.toPrimitive
            ? (hint) => {
                // Any JS-level coercion of an unresolved Output produces a
                // placeholder that *looks* like a real value but isn't:
                //
                //   - `string` / `default` hints (`${output}`, `output + ""`,
                //     `==` against a primitive) previously fell through to
                //     `self.toString()` and returned the inspect form
                //     (e.g. "tunnel.tunnelId"). The bogus string flowed
                //     into resource props and into the cloud — only
                //     surfacing as an opaque downstream error (see PR
                //     description for a real Cloudflare DNS landing).
                //
                //   - `number` hint (`+output`, `output * 2`,
                //     `Math.max(0, output)`) previously returned NaN, which
                //     propagates silently through arithmetic and lands as
                //     "the API rejected a NaN field" much later.
                //
                // All three hints fail loud at the coercion site with a
                // pointer to the right composition API.
                throw new Error(`Cannot coerce Output<${self[inspect]()}> to a ` +
                    `${hint === "number" ? "number" : "string"} via JS coercion. ` +
                    `Use Output.interpolate\`...\` or Output.map(output, fn) ` +
                    `to compose Outputs — the value isn't known until deploy time.`);
            }
            : prop === ExprSymbol
                ? self
                : prop === inspect
                    ? target[inspect]
                    : (isResourceExpr(self) || isRefExpr(self)) &&
                        self.stables &&
                        prop in self.stables
                        ? self.stables[prop]
                        : prop in self
                            ? typeof self[prop] === "function" &&
                                !("kind" in self)
                                ? new PropExpr(proxy, prop)
                                : self[prop]
                            : new PropExpr(proxy, prop),
        apply: (_, thisArg, args) => {
            if (isPropExpr(self)) {
                // Method-style combinators on an Output proxy. `map`/`apply` and
                // `mapEffect`/`effect` are aliases that mirror the standalone
                // `Output.map` / `Output.mapEffect` / `Output.flatMap` functions.
                if (self.identifier === "map" || self.identifier === "apply") {
                    return new ApplyExpr(self.expr, args[0]);
                }
                else if (self.identifier === "mapEffect" ||
                    self.identifier === "effect") {
                    return new EffectExpr(self.expr, args[0]);
                }
                else if (self.identifier === "flatMap") {
                    return new FlatMapExpr(self.expr, args[0]);
                }
            }
            return undefined;
        },
    });
    return proxy;
}
/// Evaluation
export class MissingSourceError extends Data.TaggedError("MissingSourceError") {
}
export class InvalidReferenceError extends Data.TaggedError("InvalidReferenceError") {
}
export const evaluate = (expr, upstream, ancestors = new Set()) => Effect.gen(function* () {
    if (isResource(expr)) {
        const srcId = expr.FQN;
        const src = upstream[srcId];
        if (!src) {
            // type-safety should prevent this but let the caller decide how to handle it
            return yield* new MissingSourceError({
                message: `Source ${srcId} not found`,
                srcId,
            });
        }
        return src;
    }
    else if (isOutput(expr)) {
        if (isResourceExpr(expr)) {
            const srcId = expr.src.FQN;
            const src = upstream[srcId];
            if (!src) {
                // type-safety should prevent this but let the caller decide how to handle it
                return yield* new MissingSourceError({
                    message: `Source ${srcId} not found`,
                    srcId,
                });
            }
            return src;
        }
        else if (isLiteralExpr(expr)) {
            return expr.value;
        }
        else if (isApplyExpr(expr)) {
            return expr.f(yield* evaluate(expr.expr, upstream));
        }
        else if (isEffectExpr(expr)) {
            // TODO(sam): the same effect shoudl be memoized so that it's not run multiple times
            return yield* expr.f(yield* evaluate(expr.expr, upstream));
        }
        else if (isFlatMapExpr(expr)) {
            // Resolve the source, hand it to `f` to produce a new Output, then
            // recursively evaluate that Output (flattening one level).
            const value = yield* evaluate(expr.expr, upstream);
            return yield* evaluate(expr.f(value), upstream);
        }
        else if (isAllExpr(expr)) {
            return yield* Effect.all(expr.outs.map((out) => evaluate(out, upstream)));
        }
        else if (isPropExpr(expr)) {
            return (yield* evaluate(expr.expr, upstream))?.[expr.identifier];
        }
        else if (isNamedExpr(expr)) {
            return yield* evaluate(expr.expr, upstream);
        }
        else if (isRefExpr(expr)) {
            const state = yield* yield* State.State;
            const stack = expr.stack ?? (yield* Stack).name;
            const stage = expr.stage ?? (yield* Stage);
            const resource = yield* state.get({
                stack,
                stage,
                fqn: expr.resourceId,
            });
            if (!resource) {
                return yield* Effect.fail(new InvalidReferenceError({
                    message: `Reference to '${expr.resourceId}' in stack '${stack}' and stage '${stage}' not found. Have you deployed '${stage}' or '${stack}'?`,
                    stack,
                    stage,
                    resourceId: expr.resourceId,
                }));
            }
            // RefExpr targets persisted resources; tasks aren't cross-stack
            // referenceable. Return the resource's output attrs, otherwise the
            // task's output value, otherwise undefined.
            return resource.attr ?? resource.output;
        }
        else if (isStackRefExpr(expr)) {
            const state = yield* yield* State.State;
            const stack = expr.stack;
            const stage = expr.stage ?? (yield* Stage);
            const output = yield* state.getOutput({ stack, stage });
            if (output == null) {
                return yield* Effect.fail(new InvalidReferenceError({
                    message: `Reference to stack '${stack}' at stage '${stage}' not found. Have you deployed stage '${stage}' of '${stack}'?`,
                    stack,
                    stage,
                    resourceId: stack,
                }));
            }
            return output;
        }
    }
    if (Config.isConfig(expr)) {
        // Resolve Config against the deploy environment — see resolveInput in
        // Plan.ts for rationale. `Config.redacted` resolves to a `Redacted`,
        // which stays opaque via the leaf fallthrough below.
        return yield* evaluate(yield* expr, upstream, ancestors);
    }
    else if (isPlainData(expr)) {
        if (ancestors.has(expr)) {
            return undefined;
        }
        const nested = new Set(ancestors).add(expr);
        if (Array.isArray(expr)) {
            return yield* Effect.all(expr.map((item) => evaluate(item, upstream, nested)));
        }
        return Object.fromEntries(yield* Effect.all(Object.entries(expr).map(([key, value]) => evaluate(value, upstream, nested).pipe(Effect.map((value) => [key, value])))));
    }
    // Everything else is a leaf returned by identity: Duration, Redacted,
    // Date, and effect runtime values (a Worker's `exports` carries each
    // DO's `constructor` Effect and captured `services` Context). Rebuilding
    // a class instance entry-by-entry strips its prototype, and effect
    // ≥4.0.0-beta.103's Context is cyclic (#1082). This sits after
    // `Config.isConfig` on purpose — Configs are Effects but must resolve.
    return expr;
});
export const hasOutputs = (value) => Object.keys(upstreamAny(value)).length > 0;
/**
 * Cycle guard shared by the upstream walkers. Marks `value` as visited in
 * `seen`; returns true when it was already visited (the caller returns `{}`
 * — the first visit already contributed the subtree's resources to the
 * FQN-keyed union, so skipping repeats is lossless).
 */
const alreadySeen = (value, seen) => {
    if (seen.has(value)) {
        return true;
    }
    seen.add(value);
    return false;
};
// The dependency rule (#1082): a Resource or Output IS a dependency; plain
// data (arrays, plain objects) is traversed to find them; every other value
// — class instances like effect's Effect/Layer/Context, Dates, SDK objects,
// functions — is a leaf. See `isPlainData` in Util/data.ts for why leaves
// must never be walked.
export const upstreamAny = (value, seen = new WeakSet()) => {
    if (isResource(value)) {
        return { [value.FQN]: value };
    }
    else if (isExpr(value)) {
        return upstream(value, seen);
    }
    else if (isPlainData(value)) {
        if (alreadySeen(value, seen)) {
            return {};
        }
        return Object.assign({}, ...Object.values(value).map((value) => resolveUpstream(value, seen)));
    }
    return {};
};
// TODO(sam): add a type
export const upstream = (expr, seen = new WeakSet()) => {
    if (isResource(expr)) {
        return {
            [expr.FQN]: expr,
        };
    }
    else if (isResourceExpr(expr)) {
        return {
            [expr.src.FQN]: expr.src,
        };
    }
    else if (isPropExpr(expr)) {
        return upstream(expr.expr, seen);
    }
    else if (isAllExpr(expr)) {
        return Object.assign({}, ...expr.outs.map((out) => upstream(out, seen)));
    }
    else if (isEffectExpr(expr) ||
        isApplyExpr(expr) ||
        isFlatMapExpr(expr) ||
        isNamedExpr(expr)) {
        return upstream(expr.expr, seen);
    }
    else if (isPlainData(expr)) {
        if (alreadySeen(expr, seen)) {
            return {};
        }
        return Object.values(expr)
            .map((v) => upstream(v, seen))
            .reduce(toObject, {});
    }
    return {};
};
// TODO(sam): add a type
export const resolveUpstream = (value, seen = new WeakSet()) => {
    if (isPrimitive(value)) {
        return {};
    }
    else if (isResource(value)) {
        return { [value.FQN]: value };
    }
    else if (isOutput(value)) {
        return upstream(value, seen);
    }
    else if (isPlainData(value)) {
        if (alreadySeen(value, seen)) {
            return {};
        }
        return Object.fromEntries(Object.values(value)
            .map((v) => resolveUpstream(v, seen))
            .flatMap(Object.entries));
    }
    return {};
};
const toObject = (acc, v) => ({
    ...acc,
    ...v,
});
export const log = (_value) => Effect.gen(function* () {
    // TODO(sam): implement a log effect
});
export const toEnvKey = (id, suffix) => `${replace(toUpper(id))}_${replace(toUpper(suffix))}`;
export const toUpper = (str) => str.toUpperCase();
const replace = (str) => str.replace(/-/g, "_");
//# sourceMappingURL=Output.js.map